import type { IDisplaySection } from "@lexicanon/shared"
import { useLingui } from "@lingui/react"
import { Badge, List, Text } from "@mantine/core"
import { useSpeakerColor } from "../../../contexts/SpeakerColors"
import { formatDate } from "../../../lib/format-date"
import { useProduct } from "../../../lib/tenant"
import { EditableAnalysisField } from "../../EditableAnalysisField"
import type { IEditContext } from "../interfaces"
import styles from "./LexSection.module.css"
import { SectionHeader } from "./SectionHeader"

function isRecord(val: unknown): val is Record<string, unknown> {
  return typeof val === "object" && val !== null && !Array.isArray(val)
}

function stableItemKey(item: unknown, fallback: number): string {
  if (typeof item === "string") return item
  if (isRecord(item)) {
    const id = item.id ?? item.name ?? item.description ?? item.item
    if (typeof id === "string" && id.length > 0) return id
    if (typeof id === "number") return String(id)
  }
  return `idx-${fallback}`
}

interface IProps {
  section: IDisplaySection
  data: unknown
  editContext?: IEditContext
}

export function TaggedListSection({ section, data, editContext }: IProps): React.ReactElement | null {
  const product = useProduct()

  if (!Array.isArray(data) && typeof data !== "object") return null

  // Array of items
  if (Array.isArray(data) && data.length > 0) {
    const firstItem = data[0]
    const isTaskList =
      product === "lexicanon" && typeof firstItem === "object" && firstItem !== null && "owner" in firstItem

    if (isTaskList) {
      return (
        <div>
          <SectionHeader id={section.id} label={section.label} />
          <div className={styles.taskList}>
            {data.map((item, i) => (
              <LexTaskCard
                key={`${section.id}-${stableItemKey(item, i)}`}
                item={item}
                itemIndex={i}
                sectionDataPath={section.dataPath}
                editContext={editContext}
              />
            ))}
          </div>
        </div>
      )
    }

    return (
      <div>
        <SectionHeader id={section.id} label={section.label} />
        <List spacing="xs" size="sm">
          {data.map((item, i) => (
            <List.Item key={`${section.id}-${stableItemKey(item, i)}`}>
              <TaggedItem item={item} />
            </List.Item>
          ))}
        </List>
      </div>
    )
  }

  // Object with array values (dm_notes, etc.)
  if (isRecord(data)) {
    const obj = data
    const entries = Object.entries(obj).filter(([, v]) => Array.isArray(v) || typeof v === "string")
    if (entries.length === 0) return null

    return (
      <div>
        <SectionHeader id={section.id} label={section.label} />
        {entries.map(([key, value]) => (
          <div key={key} style={{ marginBottom: 10 }}>
            <Text size="sm" fw={600} tt="capitalize" mb={4} c="dimmed">
              {key.replace(/_/g, " ")}
            </Text>
            {Array.isArray(value) ? (
              <List size="sm">
                {value.map((v, i) => (
                  <List.Item key={`${key}-${stableItemKey(v, i)}`}>
                    {typeof v === "string" ? v : <TaggedItem item={v} />}
                  </List.Item>
                ))}
              </List>
            ) : (
              <Text size="sm" c="dimmed">
                {String(value)}
              </Text>
            )}
          </div>
        ))}
      </div>
    )
  }

  return null
}

interface ILexTaskCardProps {
  item: unknown
  itemIndex: number
  sectionDataPath: string
  editContext?: IEditContext
}

function LexTaskCard({ item, itemIndex, sectionDataPath, editContext }: ILexTaskCardProps): React.ReactElement {
  const { i18n } = useLingui()
  const owner = isRecord(item) && typeof item.owner === "string" ? item.owner : ""
  const ownerColor = useSpeakerColor(owner)

  if (!isRecord(item)) return <div className={styles.taskCard}>{String(item)}</div>
  const o = item
  const description = typeof o.description === "string" ? o.description : ""
  const deadline = typeof o.deadline === "string" ? o.deadline : ""

  // Format deadline: "2026-01-15" → "Jan 15". Date-only strings get a
  // local-midnight suffix so they aren't parsed as UTC and shifted a day.
  let deadlineLabel = ""
  if (deadline) {
    const parseable = /^\d{4}-\d{2}-\d{2}$/.test(deadline) ? `${deadline}T00:00:00` : deadline
    deadlineLabel = formatDate(i18n, parseable, { year: false }) || deadline
  }

  const descriptionPath = `${sectionDataPath}.${itemIndex}.description`

  return (
    <div className={styles.taskCard}>
      {owner && (
        <Badge size="sm" variant="light" color={ownerColor} className={styles.ownerBadge}>
          {owner}
        </Badge>
      )}
      <span className={styles.taskBody}>
        {editContext ? (
          <EditableAnalysisField
            meetingId={editContext.meetingId}
            fieldPath={descriptionPath}
            value={description}
            canEdit={editContext.canEdit}
            isEdited={!!editContext.fieldEdits[descriptionPath]}
            onSaved={editContext.onEdited}
          />
        ) : (
          description
        )}
      </span>
      {deadlineLabel && <span className={styles.deadlineChip}>{deadlineLabel}</span>}
    </div>
  )
}

function TaggedItem({ item }: { item: unknown }): React.ReactElement {
  if (typeof item === "string") return <Text size="sm">{item}</Text>
  if (!isRecord(item)) return <Text size="sm">{String(item)}</Text>

  const o = item
  const labelVal = o.name ?? o.description ?? o.item ?? ""
  const label = typeof labelVal === "string" ? labelVal : String(labelVal)
  const tagVal = o.status ?? o.owner ?? o.deadline ?? ""
  const tag = typeof tagVal === "string" ? tagVal : String(tagVal)
  const detailVal = o.progress ?? o.note ?? o.significance ?? ""
  const detail = typeof detailVal === "string" ? detailVal : String(detailVal)

  return (
    <span>
      {tag && (
        <Text component="span" size="xs" fw={600} c="dimmed" tt="uppercase" mr={6}>
          [{tag}]
        </Text>
      )}
      <Text component="span" size="sm">
        {label}
      </Text>
      {detail && (
        <Text size="xs" c="dimmed" mt={2}>
          {detail}
        </Text>
      )}
    </span>
  )
}
